import type { AnchorsUpsertResponseModel } from '@/shared/generated'
import type z from 'zod'

import React, { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { isEmpty } from 'lodash/fp'
import {
	AlertTriangleIcon,
	CheckCircle2Icon,
	ChevronLeftIcon,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useImportAnchorAssortmentFromFileMutation } from '@/entities/anchor'

import { ValidationErrorsList } from '@/shared/components/common/ValidationErrorsList'
import { FormProvider, RHFCheckbox } from '@/shared/components/HookForm'
import RHFFileDropArea from '@/shared/components/HookForm/RHFFileDropArea'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Table,
	TableBody,
	TableCell,
	TableRow,
} from '@/shared/components/ui/table'
import { ampli } from '@/shared/lib/ampli'

import { downloadImportTemplate } from '../lib/helpers'
import { ImportFromFileSchema } from '../model/contract'

type ImportDataFromFileModalContentProps = {
	close(): void
	isOpen: boolean
}

type ImportFromFileFormData = z.infer<typeof ImportFromFileSchema>

function ImportDataFromFileModalContent({
	close,
	isOpen,
}: ImportDataFromFileModalContentProps) {
	const { t } = useTranslation()

	const [screen, setScreen] = useState<
		'form' | 'proccess' | 'errors' | 'result'
	>('form')
	const queryClient = useQueryClient()
	const uploadFile = useImportAnchorAssortmentFromFileMutation({
		onMutate: () => {
			setScreen('proccess')
		},
		onError: () => {
			setScreen('errors')
		},
		onSuccess: () => {
			setScreen('result')
		},
	})
	const methods = useForm<ImportFromFileFormData>({
		defaultValues: {
			ignore_empties: true,
		},
		resolver: zodResolver(ImportFromFileSchema),
	})
	const handleCloseAndFinish = () => {
		close()
		queryClient.invalidateQueries({
			queryKey: ['anchor-assortment'],
		})
	}
	const handleBackToImport = () => setScreen('form')
	const handleDownloadTemplate = () => {
		ampli.anchoringImportFromFileDownloadTemplate()
		downloadImportTemplate(t)
	}
	const onSubmit = (formData: ImportFromFileFormData) => {
		ampli.anchoringImportFromFileUpload()
		uploadFile.mutate({
			ignore_empties: formData.ignore_empties,
			file: formData.product_diffs[0],
		})
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='flex max-h-[80vh] flex-col overflow-hidden'>
				{screen === 'form' && (
					<ModalHeader>
						<ModalTitle>{t('app.import_from_file')}</ModalTitle>
						<ModalDescription>{t('anchoring.import.title')}</ModalDescription>
					</ModalHeader>
				)}
				<ModalBody className='flex flex-col overflow-y-auto'>
					<AnimatePresence mode='wait'>
						{screen === 'errors' && (
							<StepContent key='errors'>
								<Empty>
									<EmptyHeader>
										<EmptyMedia variant='icon'>
											<AlertTriangleIcon />
										</EmptyMedia>
										<EmptyTitle>{t('anchoring.import.error.title')}</EmptyTitle>
										<EmptyDescription>
											{t('anchoring.import.error.subtitle')}
										</EmptyDescription>
									</EmptyHeader>
								</Empty>
								{uploadFile.isError &&
								!isEmpty(uploadFile.error?.response?.data?.detail) ? (
									<div className='flex flex-col overflow-y-auto'>
										<h4 className='text-sm font-bold'>
											{t('general_error_types')}
										</h4>
										<div className='mt-2 overflow-y-auto'>
											<ValidationErrorsList
												errors={uploadFile.error?.response?.data?.detail}
											/>
										</div>
									</div>
								) : (
									<div className='space-y-2'>
										<h4>{t('general_possible_reasons')}:</h4>
										<p
											className='ml-5'
											dangerouslySetInnerHTML={{
												__html: t('og_import_error_hint_html'),
											}}
										/>
									</div>
								)}
							</StepContent>
						)}
						{screen === 'result' && (
							<ResultContent uploadFile={uploadFile.data} />
						)}
						{screen === 'proccess' && <ProcessingContent />}
						{screen === 'form' && (
							<StepContent key='form'>
								<FormProvider
									methods={methods}
									onSubmit={methods.handleSubmit(onSubmit)}
									className='flex flex-col gap-3'
								>
									<RHFFileDropArea className='min-h-28' name='product_diffs' />
									<RHFCheckbox
										name='ignore_empties'
										label={t('modal_import_ignore_empties')}
									/>
									<h4 className='text-sm font-semibold'>
										{t('modal_import_guide')}
									</h4>
									<ul className='mb-4 list-inside list-decimal text-sm'>
										<li>
											{t('og_import_step_1')}
											<LinkButton
												variant='brand'
												onClick={handleDownloadTemplate}
												className='ml-1 text-primary'
											>
												{t('og_import_types')}
											</LinkButton>
										</li>
										<li>{t('og_import_step_2')}</li>
										<li>{t('og_import_step_3')}</li>
									</ul>
								</FormProvider>
							</StepContent>
						)}
					</AnimatePresence>
				</ModalBody>
				{screen === 'form' && (
					<ModalFooter>
						<ModalClose className='mr-auto' asChild>
							<Button>{t('general_cancel')}</Button>
						</ModalClose>
						<Button
							variant='primary'
							disabled={!methods.watch('product_diffs')}
							onClick={methods.handleSubmit(onSubmit)}
						>
							{t('app.update', { defaultValue: 'Update' })}
						</Button>
					</ModalFooter>
				)}
				{screen === 'result' && (
					<ModalFooter>
						<Button variant='primary' onClick={handleCloseAndFinish}>
							{t('general_close_and_finish')}
						</Button>
					</ModalFooter>
				)}
				{screen === 'errors' && (
					<ModalFooter>
						<Button onClick={handleBackToImport}>
							<ChevronLeftIcon />
							{t('general_back')}
						</Button>
					</ModalFooter>
				)}
			</ModalContent>
		</Modal>
	)
}
function ProcessingContent() {
	const { t } = useTranslation()

	return (
		<StepContent key='proccess'>
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Spinner />
					</EmptyMedia>
					<EmptyTitle>{t('og_bulk_import_updating')}</EmptyTitle>
					<EmptyDescription>
						{t('og_bulk_import_updating_hint')}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		</StepContent>
	)
}
function ResultContent({
	uploadFile,
}: {
	uploadFile: AnchorsUpsertResponseModel | undefined
}) {
	const { t } = useTranslation()

	return (
		<StepContent key='result'>
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon' className='bg-green-500/20'>
						<CheckCircle2Icon className='text-green-500' />
					</EmptyMedia>
					<EmptyTitle>{t('anchoring.import.result.title')}</EmptyTitle>
				</EmptyHeader>
				<EmptyContent>
					{uploadFile && (
						<div className='overflow-hidden rounded-md border'>
							<Table data-slot='table' className='text-left text-sm'>
								<TableBody>
									<TableRow>
										<TableCell className='bg-muted/50 font-medium whitespace-nowrap'>
											{t('anchoring.import.result.rows_inserted')}
										</TableCell>
										<TableCell className='w-32 whitespace-nowrap'>
											{uploadFile?.rows_inserted}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className='bg-muted/50 font-medium whitespace-nowrap'>
											{t('anchoring.import.result.rows_updated')}
										</TableCell>
										<TableCell className='w-32 whitespace-nowrap'>
											{uploadFile?.rows_updated}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>
					)}
				</EmptyContent>
			</Empty>
		</StepContent>
	)
}

export function ImportAnchorAssortmentFromFileModal(
	props: ImportDataFromFileModalContentProps
) {
	return <ImportDataFromFileModalContent {...props} />
}

function StepContent({ children }: { children: React.ReactNode }) {
	return (
		<motion.div
			initial={{ opacity: 0, x: 100 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: -30 }}
			className='flex size-full flex-col'
			transition={{
				duration: 0.2,
			}}
		>
			{children}
		</motion.div>
	)
}
